import React, { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  GripVertical,
  Upload,
  Save,
  Copy,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  Tag,
  Clock,
  CheckCircle2,
  FileText,
  Image
} from 'lucide-react';
import Header from '../layout/Header';
import Modal from '../common/Modal';
import Button from '../common/Button';
import {
  addCurriculumItem,
  deleteCurriculumItem,
  getCurriculumItems,
  updateCurriculumItem,
  reorderCurriculumItems,
  uploadFile
} from '../../firebase/firestore';
import { AUDIENCE_OPTIONS, CURRICULUM_DAY_OPTIONS, DAY_DISPLAY, GRADE_BANDS, SPORT_CATEGORY_OPTIONS, getGradeBand } from '../../utils/curriculumConstants';
import { getCourses, addCourse, updateCourse, deleteCourse } from '../../firebase/trainingAPI';
import CourseForm from './CourseForm';
import QuizManagement from './QuizManagement';

const DEFAULT_FORM = {
  title: '',
  shortDescription: '',
  sportCategory: '',
  audience: '',
  youtubeLink: '',
  resourceLinks: [{ label: '', url: '' }],
  attachmentUrl: '',
  attachmentName: '',
  active: false,
  weekLabel: '',
  dayOfWeek: '',
  objective: '',
  equipment: '',
  keyCoachingPoints: '',
  durationMinutes: '',
  courseId: ''
};

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

const normalizeItems = (items = []) =>
  [...items]
    .map((item, index) => ({
      ...item,
      order: Number.isFinite(item.order) ? item.order : index + 1,
      resourceLinks: Array.isArray(item.resourceLinks) ? item.resourceLinks : [],
      active: Boolean(item.active)
    }))
    .sort((a, b) => a.order - b.order);

/* Thumbnails: explicit thumbnailUrl, image attachment, or YouTube preview */
const getYoutubeVideoId = (url = '') => {
  const value = String(url).trim();
  if (!value) return '';
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&#]+)/i,
    /(?:youtube\.com\/embed\/)([^&#?/]+)/i,
    /(?:youtu\.be\/)([^&#?/]+)/i
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return match[1];
  }
  return '';
};

const getYoutubeThumbnailUrl = (youtubeLink = '') => {
  const id = getYoutubeVideoId(youtubeLink);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : '';
};

const isLikelyImageUrl = (url = '') => {
  const u = String(url).trim().toLowerCase();
  if (!u.startsWith('http')) return false;
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(u) || /image%2f/i.test(u);
};

const isImageAttachment = (item) => {
  if (!item?.attachmentUrl) return false;
  const name = String(item.attachmentName || '').toLowerCase();
  if (/\.(jpe?g|png|gif|webp|bmp|svg)$/.test(name)) return true;
  return isLikelyImageUrl(item.attachmentUrl);
};

const isLikelyPdfAttachment = (item) => {
  if (!item?.attachmentUrl) return false;
  const name = String(item.attachmentName || '').toLowerCase();
  if (/\.pdf$/.test(name)) return true;
  const u = String(item.attachmentUrl).trim().toLowerCase();
  return /\.pdf(\?|#|$)/i.test(u) || /application%2fpdf/i.test(u);
};

/** Resolve preview URL for table row: explicit thumb, uploaded image, or YouTube still */
const getCurriculumListThumbnailSrc = (item) => {
  const explicit = String(item?.thumbnailUrl || '').trim();
  if (explicit) return explicit;
  if (item?.attachmentUrl && isImageAttachment(item)) return String(item.attachmentUrl).trim();
  return getYoutubeThumbnailUrl(item?.youtubeLink) || '';
};

const CurriculumRowThumbnail = ({ item }) => {
  const src = useMemo(() => getCurriculumListThumbnailSrc(item), [
    item?.id,
    item?.thumbnailUrl,
    item?.attachmentUrl,
    item?.attachmentName,
    item?.youtubeLink
  ]);
  const [broken, setBroken] = useState(false);
  const pdfOnly = !src && item?.attachmentUrl && isLikelyPdfAttachment(item);

  if (!src || broken) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-400 flex items-center justify-center">
        {pdfOnly ? (
          <FileText size={14} className="text-white" />
        ) : (
          <Image size={14} className="text-white opacity-90" />
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      className="w-full h-full object-cover"
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
    />
  );
};

// Sport category badge colors
const SPORT_COLORS = {
  'Soccer': { bg: '#EEF2FF', text: '#4338CA', dot: '#6366F1' },
  'Flag Football': { bg: '#FFF7ED', text: '#C2410C', dot: '#F97316' },
  'Cheer': { bg: '#FDF4FF', text: '#7E22CE', dot: '#A855F7' },
  'Basketball': { bg: '#FFF3E0', text: '#B45309', dot: '#F59E0B' },
  'Baseball': { bg: '#F0FDF4', text: '#15803D', dot: '#22C55E' },
};

const getSportStyle = (sport) => {
  return SPORT_COLORS[sport] || { bg: '#F3F4F6', text: '#374151', dot: '#9CA3AF' };
};

const SportBadge = ({ sport }) => {
  const style = getSportStyle(sport);
  const icons = {
    'Soccer': '⚽',
    'Flag Football': '🏈',
    'Cheer': '📣',
    'Basketball': '🏀',
    'Baseball': '⚾',
  };
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      <span>{icons[sport] || '🏷'}</span>
      {sport || 'General'}
    </span>
  );
};

const GradeBandCell = ({ value }) => {
  if (!value) return <span className="text-gray-400">—</span>;
  const band = getGradeBand(value);
  if (!band) {
    return <span className="text-sm text-gray-700">{value}</span>;
  }
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-sm text-gray-800 font-medium">{band.short}</span>
      <span className="text-[11px] text-gray-500">
        {band.level ? `Level ${band.level}` : 'All Coaches'}
      </span>
    </div>
  );
};

const StatusBadge = ({ active }) => {
  if (active) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
        Published
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
      Unpublished
    </span>
  );
};

const CurriculumForm = ({ initialData, onSave, onCancel, saving, courses = [] }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        shortDescription: initialData.shortDescription || '',
        sportCategory: initialData.sportCategory || '',
        audience: initialData.audience || '',
        youtubeLink: initialData.youtubeLink || '',
        resourceLinks:
          initialData.resourceLinks && initialData.resourceLinks.length > 0
            ? initialData.resourceLinks
            : [{ label: '', url: '' }],
        attachmentUrl: initialData.attachmentUrl || '',
        attachmentName: initialData.attachmentName || '',
        active: Boolean(initialData.active),
        weekLabel: initialData.weekLabel || '',
        dayOfWeek: initialData.dayOfWeek || '',
        objective: initialData.objective || '',
        equipment: initialData.equipment || '',
        keyCoachingPoints: initialData.keyCoachingPoints || '',
        durationMinutes: initialData.durationMinutes != null && initialData.durationMinutes !== ''
          ? String(initialData.durationMinutes)
          : '',
        courseId: initialData.courseId || ''
      });
      setAttachmentFile(null);
      setErrors({});
    }
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleResourceLinkChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      resourceLinks: prev.resourceLinks.map((link, idx) =>
        idx === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const addResourceLinkRow = () => {
    setFormData((prev) => ({
      ...prev,
      resourceLinks: [...prev.resourceLinks, { label: '', url: '' }]
    }));
  };

  const removeResourceLinkRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      resourceLinks:
        prev.resourceLinks.length === 1
          ? [{ label: '', url: '' }]
          : prev.resourceLinks.filter((_, idx) => idx !== index)
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.title.trim()) nextErrors.title = 'Title is required';
    if (!formData.shortDescription.trim()) nextErrors.shortDescription = 'Short description is required';
    if (!formData.sportCategory.trim()) nextErrors.sportCategory = 'Sport/Category is required';
    if (!formData.audience.trim()) nextErrors.audience = 'Audience is required';
    if (!formData.youtubeLink.trim()) {
      nextErrors.youtubeLink = 'YouTube link is required';
    } else if (!/^https?:\/\/.+/i.test(formData.youtubeLink)) {
      nextErrors.youtubeLink = 'Please enter a valid URL';
    }
    const invalidLink = formData.resourceLinks.find(
      (link) => link.url.trim() && !/^https?:\/\/.+/i.test(link.url.trim())
    );
    if (invalidLink) nextErrors.resourceLinks = 'Each resource URL must be a valid http(s) link';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAttachmentFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    const cleanedResourceLinks = formData.resourceLinks
      .map((link) => ({ label: link.label.trim(), url: link.url.trim() }))
      .filter((link) => link.url);
    const payload = {
      title: formData.title.trim(),
      shortDescription: formData.shortDescription.trim(),
      sportCategory: formData.sportCategory.trim(),
      audience: formData.audience.trim(),
      youtubeLink: formData.youtubeLink.trim(),
      resourceLinks: cleanedResourceLinks,
      active: formData.active,
      attachmentUrl: formData.attachmentUrl || '',
      attachmentName: formData.attachmentName || '',
      weekLabel: formData.weekLabel.trim(),
      dayOfWeek: formData.dayOfWeek.trim(),
      objective: formData.objective.trim(),
      equipment: formData.equipment.trim(),
      keyCoachingPoints: formData.keyCoachingPoints.trim(),
      durationMinutes: formData.durationMinutes.trim() === '' ? '' : Number(formData.durationMinutes),
      courseId: formData.courseId || ''
    };
    if (attachmentFile) {
      try {
        setUploading(true);
        const uploaded = await uploadFile(attachmentFile, 'document');
        payload.attachmentUrl = uploaded.url || '';
        payload.attachmentName = attachmentFile.name;
      } catch (error) {
        setErrors((prev) => ({ ...prev, attachmentFile: error.message || 'Failed to upload attachment' }));
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
    }
    onSave(payload);
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Course</label>
        <div className="relative">
          <select
            value={formData.courseId}
            onChange={(e) => handleChange('courseId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none pr-9 bg-white"
          >
            <option value="">— No Course —</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
          <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input type="text" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} className={inputClass} placeholder="Lesson title" />
          {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">YouTube Link *</label>
          <input type="url" value={formData.youtubeLink} onChange={(e) => handleChange('youtubeLink', e.target.value)} className={inputClass} placeholder="https://www.youtube.com/watch?v=..." />
          {errors.youtubeLink && <p className="text-xs text-red-600 mt-1">{errors.youtubeLink}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sport / Category *</label>
          <div className="relative">
            <select
              value={formData.sportCategory}
              onChange={(e) => handleChange('sportCategory', e.target.value)}
              className={`${inputClass} appearance-none pr-9 bg-white`}
            >
              <option value="">Select sport / category...</option>
              {SPORT_CATEGORY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
              {formData.sportCategory && !SPORT_CATEGORY_OPTIONS.includes(formData.sportCategory) && (
                <option value={formData.sportCategory}>{formData.sportCategory} (legacy)</option>
              )}
            </select>
            <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          {errors.sportCategory && <p className="text-xs text-red-600 mt-1">{errors.sportCategory}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Grade Band *</label>
          <div className="relative">
            <select
              value={formData.audience}
              onChange={(e) => handleChange('audience', e.target.value)}
              className={`${inputClass} appearance-none pr-9 bg-white`}
            >
              <option value="">Select a grade band...</option>
              {GRADE_BANDS.map((band) => (
                <option key={band.value} value={band.value}>
                  {band.value}
                </option>
              ))}
              {formData.audience && !GRADE_BANDS.some((b) => b.value === formData.audience) && (
                <option value={formData.audience}>{formData.audience} (legacy)</option>
              )}
            </select>
            <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          <p className="text-xs text-gray-500 mt-1">Targets the curriculum to a specific student level. Choose <span className="font-medium">All Coaches</span> for content that applies to every group.</p>
          {errors.audience && <p className="text-xs text-red-600 mt-1">{errors.audience}</p>}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Short Description *</label>
        <textarea rows={3} value={formData.shortDescription} onChange={(e) => handleChange('shortDescription', e.target.value)} className={inputClass} placeholder="Brief overview of this curriculum item" />
        {errors.shortDescription && <p className="text-xs text-red-600 mt-1">{errors.shortDescription}</p>}
      </div>

      <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50/50">
        <h4 className="text-sm font-semibold text-gray-800">Weekly schedule (Coach Portal)</h4>
        <p className="text-xs text-gray-500">Optional. Used to group lessons by week and day in the coach training view.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Week label</label>
            <input
              type="text"
              value={formData.weekLabel}
              onChange={(e) => handleChange('weekLabel', e.target.value)}
              className={inputClass}
              placeholder="e.g. Week 1: Fundamentals"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Day slot</label>
            <div className="relative">
              <select
                value={formData.dayOfWeek}
                onChange={(e) => handleChange('dayOfWeek', e.target.value)}
                className={`${inputClass} appearance-none pr-9 bg-white`}
              >
                <option value="">Not set (library order only)</option>
                {CURRICULUM_DAY_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {DAY_DISPLAY[d] || d}
                  </option>
                ))}
              </select>
              <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
            <input
              type="number"
              min={0}
              value={formData.durationMinutes}
              onChange={(e) => handleChange('durationMinutes', e.target.value)}
              className={inputClass}
              placeholder="e.g. 10"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Learning objective</label>
          <textarea
            rows={2}
            value={formData.objective}
            onChange={(e) => handleChange('objective', e.target.value)}
            className={inputClass}
            placeholder="What coaches should achieve with this lesson"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Equipment needed</label>
          <textarea
            rows={3}
            value={formData.equipment}
            onChange={(e) => handleChange('equipment', e.target.value)}
            className={inputClass}
            placeholder="One item per line (e.g. Soccer balls, cones)"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Key coaching points</label>
          <textarea
            rows={4}
            value={formData.keyCoachingPoints}
            onChange={(e) => handleChange('keyCoachingPoints', e.target.value)}
            className={inputClass}
            placeholder="One bullet per line"
          />
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800">Resource Links</h4>
          <button type="button" onClick={addResourceLinkRow} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
            <Plus size={13} /> Add Link
          </button>
        </div>
        {formData.resourceLinks.map((link, index) => (
          <div key={`resource-link-${index}`} className="flex gap-2">
            <input type="text" value={link.label} onChange={(e) => handleResourceLinkChange(index, 'label', e.target.value)} className="flex-none w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Label" />
            <input type="url" value={link.url} onChange={(e) => handleResourceLinkChange(index, 'url', e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="https://..." />
            <button type="button" onClick={() => removeResourceLinkRow(index)} className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {errors.resourceLinks && <p className="text-xs text-red-600">{errors.resourceLinks}</p>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Optional Attachment</label>
          <input type="file" onChange={handleFileSelect} className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
          {(attachmentFile || formData.attachmentUrl) && (
            <p className="text-xs text-gray-500 mt-1">{attachmentFile ? attachmentFile.name : formData.attachmentName || 'Attachment uploaded'}</p>
          )}
          {errors.attachmentFile && <p className="text-xs text-red-600 mt-1">{errors.attachmentFile}</p>}
        </div>
        <div className="flex items-center">
          <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
            <input type="checkbox" checked={formData.active} onChange={(e) => handleChange('active', e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4" />
            Publish now (visible in Coach Portal)
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
        <button type="button" onClick={onCancel} disabled={saving || uploading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          Cancel
        </button>
        <button type="submit" disabled={saving || uploading} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          <Save size={14} />
          {saving || uploading ? 'Saving...' : 'Save Curriculum Item'}
        </button>
      </div>
    </form>
  );
};

const Curriculum = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState('All Sports');
  const [gradeFilter, setGradeFilter] = useState('All Grade Groups');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [draggingId, setDraggingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('Curriculum Library');
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseSaving, setCourseSaving] = useState(false);

  const tabs = ['Curriculum Library', 'Categories', 'Grade Groups', 'Resources', 'Courses', 'Quizzes'];

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await getCurriculumItems();
      setItems(normalizeItems(data));
    } catch (error) {
      console.error('Failed loading curriculum:', error);
      alert(`Error loading curriculum: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  const loadCourses = async () => {
    try {
      setCoursesLoading(true);
      const data = await getCourses();
      setCourses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed loading courses:', error);
    } finally {
      setCoursesLoading(false);
    }
  };

  useEffect(() => { loadCourses(); }, []);

  const handleCourseSave = async (payload) => {
    try {
      setCourseSaving(true);
      if (editingCourse?.id) {
        await updateCourse(editingCourse.id, payload);
      } else {
        await addCourse(payload);
      }
      setIsCourseModalOpen(false);
      setEditingCourse(null);
      await loadCourses();
    } catch (error) {
      console.error('Failed saving course:', error);
      alert(`Save failed: ${error.message}`);
    } finally {
      setCourseSaving(false);
    }
  };

  const handleDeleteCourse = async (course) => {
    if (!window.confirm(`Delete course "${course.title}"? This cannot be undone.`)) return;
    try {
      await deleteCourse(course.id);
      await loadCourses();
    } catch (error) {
      console.error('Failed deleting course:', error);
      alert(`Delete failed: ${error.message}`);
    }
  };

  const persistOrder = async (nextItems) => {
    const payload = nextItems.map((item, index) => ({ id: item.id, order: index + 1 }));
    try {
      await reorderCurriculumItems(payload);
    } catch (error) {
      console.error('Failed persisting order:', error);
      alert(`Could not save order: ${error.message}`);
      loadItems();
    }
  };

  const handleOrderChange = async (item, direction) => {
    const index = items.findIndex((i) => i.id === item.id);
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === items.length - 1) return;
    const nextItems = [...items];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [nextItems[index], nextItems[swapIndex]] = [nextItems[swapIndex], nextItems[index]];
    const reOrdered = normalizeItems(nextItems.map((i, idx) => ({ ...i, order: idx + 1 })));
    setItems(reOrdered);
    await persistOrder(reOrdered);
  };

  const handleDrop = async (targetId) => {
    if (!draggingId || draggingId === targetId) { setDraggingId(null); return; }
    const nextItems = [...items];
    const fromIndex = nextItems.findIndex((item) => item.id === draggingId);
    const toIndex = nextItems.findIndex((item) => item.id === targetId);
    if (fromIndex < 0 || toIndex < 0) { setDraggingId(null); return; }
    const [moved] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, moved);
    const reOrdered = normalizeItems(nextItems.map((item, idx) => ({ ...item, order: idx + 1 })));
    setItems(reOrdered);
    setDraggingId(null);
    await persistOrder(reOrdered);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await deleteCurriculumItem(item.id);
      const remaining = items.filter((entry) => entry.id !== item.id);
      const reOrdered = normalizeItems(remaining.map((entry, idx) => ({ ...entry, order: idx + 1 })));
      setItems(reOrdered);
      await persistOrder(reOrdered);
    } catch (error) {
      console.error('Failed deleting curriculum item:', error);
      alert(`Delete failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishToggle = async (item) => {
    try {
      await updateCurriculumItem(item.id, { active: !item.active });
      setItems((prev) => prev.map((entry) => (entry.id === item.id ? { ...entry, active: !entry.active } : entry)));
    } catch (error) {
      console.error('Failed toggling publish state:', error);
      alert(`Publish update failed: ${error.message}`);
    }
  };

  const handleDuplicate = async (item) => {
    try {
      const { id, ...rest } = item;
      await addCurriculumItem({ ...rest, title: `${item.title} (Copy)`, order: items.length + 1, active: false });
      await loadItems();
    } catch (error) {
      console.error('Failed duplicating item:', error);
      alert(`Duplicate failed: ${error.message}`);
    }
  };

  const openCreateModal = () => { setEditingItem(null); setIsModalOpen(true); };
  const openEditModal = (item) => { setEditingItem(item); setIsModalOpen(true); };

  const handleSave = async (payload) => {
    try {
      setSaving(true);
      if (editingItem?.id) {
        await updateCurriculumItem(editingItem.id, payload);
      } else {
        await addCurriculumItem({ ...payload, order: items.length + 1 });
      }
      setIsModalOpen(false);
      setEditingItem(null);
      await loadItems();
    } catch (error) {
      console.error('Failed saving curriculum item:', error);
      alert(`Save failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const sports = useMemo(() => {
    const values = Array.from(new Set([...SPORT_CATEGORY_OPTIONS, ...items.map((i) => i.sportCategory).filter(Boolean)]));
    return values.sort((a, b) => a.localeCompare(b));
  }, [items]);

  const gradeGroups = useMemo(() => {
    const values = Array.from(new Set([...AUDIENCE_OPTIONS, ...items.map((i) => i.audience).filter(Boolean)]));
    return values.sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      const inSearch = !term || item.title?.toLowerCase().includes(term) || item.shortDescription?.toLowerCase().includes(term) || item.sportCategory?.toLowerCase().includes(term) || item.audience?.toLowerCase().includes(term);
      const inSport = sportFilter === 'All Sports' || item.sportCategory === sportFilter;
      const inGrade = gradeFilter === 'All Grade Groups' || item.audience === gradeFilter;
      const inStatus = statusFilter === 'All Status' || (statusFilter === 'Published' ? item.active : !item.active);
      return inSearch && inSport && inGrade && inStatus;
    });
  }, [items, search, sportFilter, gradeFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const paginatedItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const publishedCount = items.filter((i) => i.active).length;
  const unpublishedCount = items.filter((i) => !i.active).length;
  const categoriesCount = useMemo(() => new Set(items.map((i) => i.sportCategory).filter(Boolean)).size, [items]);

  const statCards = [
    { label: 'Total Items', value: items.length, icon: <BookOpen size={20} className="text-blue-600" />, iconBg: 'bg-blue-100' },
    { label: 'Published', value: publishedCount, icon: <CheckCircle2 size={20} className="text-emerald-600" />, iconBg: 'bg-emerald-100' },
    { label: 'Unpublished', value: unpublishedCount, icon: <Clock size={20} className="text-amber-500" />, iconBg: 'bg-amber-100' },
    { label: 'Categories', value: categoriesCount, icon: <Tag size={20} className="text-purple-600" />, iconBg: 'bg-purple-100' },
  ];

  const selectClass = "px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700 appearance-none pr-8";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Curriculum</h1>
            <p className="text-sm text-gray-500 mt-0.5">Create, manage and organize all curriculum items.</p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <Plus size={16} />
            Add New Curriculum
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mt-4 border-b border-gray-200 -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab !== 'Courses' && activeTab !== 'Quizzes' && (
        <div className="px-6 py-6 space-y-5">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card) => (
            <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-none ${card.iconBg}`}>
                {card.icon}
              </div>
              <div>
                <p className="text-xs text-gray-500">{card.label}</p>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Filters */}
          <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-48">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                placeholder="Search curriculum..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="relative">
              <select value={sportFilter} onChange={(e) => { setSportFilter(e.target.value); setCurrentPage(1); }} className={selectClass}>
                <option>All Sports</option>
                {sports.map((s) => <option key={s}>{s}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select value={gradeFilter} onChange={(e) => { setGradeFilter(e.target.value); setCurrentPage(1); }} className={selectClass}>
                <option>All Grade Groups</option>
                {gradeGroups.map((g) => {
                  const band = getGradeBand(g);
                  return (
                    <option key={g} value={g}>
                      {band ? `${band.short} · ${band.label}` : g}
                    </option>
                  );
                })}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className={selectClass}>
                <option>All Status</option>
                <option>Published</option>
                <option>Unpublished</option>
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            <button className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <SlidersHorizontal size={14} />
              Filters
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : paginatedItems.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <BookOpen className="mx-auto mb-3 text-gray-300" size={40} />
              <p className="text-sm">No curriculum items found.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="w-8 px-3 py-3"></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Sport / Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Grade Group</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Order</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedItems.map((item) => (
                  <tr
                    key={item.id}
                    draggable
                    onDragStart={() => setDraggingId(item.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(item.id)}
                    onDragEnd={() => setDraggingId(null)}
                    className={`group hover:bg-gray-50 transition-colors ${draggingId === item.id ? 'opacity-50 bg-blue-50' : ''}`}
                  >
                    <td className="px-3 py-3">
                      <GripVertical size={15} className="text-gray-300 group-hover:text-gray-400 cursor-grab" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-10 rounded-md bg-gray-200 flex-none overflow-hidden ring-1 ring-gray-200/80 shadow-sm">
                          <CurriculumRowThumbnail item={item} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{item.title}</p>
                          <p className="text-xs text-gray-500 truncate max-w-xs">{item.shortDescription}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <SportBadge sport={item.sportCategory} />
                    </td>
                    <td className="px-4 py-3">
                      <GradeBandCell value={item.audience} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge active={item.active} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-700 w-5 text-center">{item.order}</span>
                        <div className="flex flex-col">
                          <button
                            onClick={() => handleOrderChange(item, 'up')}
                            className="text-gray-400 hover:text-gray-700 leading-none"
                          >
                            <ChevronUp size={13} />
                          </button>
                          <button
                            onClick={() => handleOrderChange(item, 'down')}
                            className="text-gray-400 hover:text-gray-700 leading-none"
                          >
                            <ChevronDown size={13} />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Edit */}
                        <button
                          onClick={() => openEditModal(item)}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        {/* Duplicate */}
                        <button
                          onClick={() => handleDuplicate(item)}
                          className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Duplicate"
                        >
                          <Copy size={15} />
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {!loading && filteredItems.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3 text-sm">
              <p className="text-gray-500 text-xs">
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredItems.length)} to{' '}
                {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} items
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) page = i + 1;
                  else if (currentPage <= 3) page = i + 1;
                  else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                  else page = currentPage - 2 + i;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 rounded-lg text-xs font-medium ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronRight size={14} />
                </button>
                <div className="relative ml-2">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="pl-2 pr-7 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-gray-600 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {ITEMS_PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                  <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Courses Tab */}
      {activeTab === 'Courses' && (
        <div className="px-6 py-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Training Courses</h2>
              <p className="text-sm text-gray-500">Manage training courses that curriculum items can be assigned to.</p>
            </div>
            <button
              onClick={() => { setEditingCourse(null); setIsCourseModalOpen(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Plus size={16} />
              Add Course
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            {coursesLoading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <BookOpen className="mx-auto mb-3 text-gray-300" size={40} />
                <p className="text-sm">No courses created yet.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Assessment</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {courses.map((course) => (
                    <tr key={course.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-900">{course.title}</td>
                      <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{course.description || '—'}</td>
                      <td className="px-4 py-3">
                        {course.isRequired ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Required</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Optional</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {course.questionCount && (
                            <span className="text-sm text-gray-700">{course.questionCount} Q</span>
                          )}
                          {course.passingScore && (
                            <span className="text-xs text-gray-500">Pass: {course.passingScore}%</span>
                          )}
                          {course.isRequired && course.quizRequired && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700">Quiz req.</span>
                          )}
                          {!course.questionCount && !course.passingScore && <span className="text-gray-400 text-sm">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {course.createdAt
                          ? new Date(course.createdAt.toDate ? course.createdAt.toDate() : course.createdAt).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => { setEditingCourse(course); setIsCourseModalOpen(true); }}
                            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => handleDeleteCourse(course)}
                            className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Course Modal */}
          <Modal
            isOpen={isCourseModalOpen}
            onClose={() => { if (!courseSaving) { setIsCourseModalOpen(false); setEditingCourse(null); } }}
            title={editingCourse ? `Edit Course: ${editingCourse.title}` : 'Add Course'}
            size="md"
          >
            <CourseForm
              initialData={editingCourse}
              onSave={handleCourseSave}
              onCancel={() => { setIsCourseModalOpen(false); setEditingCourse(null); }}
              saving={courseSaving}
            />
          </Modal>
        </div>
      )}

      {/* Quizzes Tab */}
      {activeTab === 'Quizzes' && <QuizManagement courses={courses} />}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { if (!saving) { setIsModalOpen(false); setEditingItem(null); } }}
        title={editingItem ? `Edit Curriculum: ${editingItem.title}` : 'Add Curriculum Item'}
        size="lg"
      >
        <CurriculumForm
          initialData={editingItem || DEFAULT_FORM}
          onSave={handleSave}
          onCancel={() => { setIsModalOpen(false); setEditingItem(null); }}
          saving={saving}
          courses={courses}
        />
      </Modal>
    </div>
  );
};

export default Curriculum;